import React, { useState } from "react"
import { Form, Modal, Button, Tab, Tabs } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import IntlProps from "../../state/helpers/IntlProps"
import { isError } from "../../state/helpers/Error"
import Shortcut, { ShortcutDto, SHORTCUT_TYPE } from "../../state/shortcut/Shortcut"
import ShortcutService from "../../state/shortcut/ShortcutService"
import ShortcutFilterSettings from "./ShortcutFilterSettings"
import ShortcutActionSettings from "./ShortcutActionSettings"
import { getIconValue, SHORTCUT_ACTION_TYPE } from "../../state/shortcut/ShortcutAction"

type OwnProps = {
    shortcut?: Shortcut,
    ranks?: {
        "shortcut-filter": number
        "shortcut-action": number
    }
    onClose: () => void,
    handleShortcutsList: (shortcutType: string, updatedShortcut: Shortcut, handleType: string) => void,
}

const DEFAULT_ATTRIBUTES = {
    color: "primary"
}

const ShortcutsSettingsModal: React.FC<OwnProps & IntlProps> = (props) => {

    const [errorMsg, setErrorMsg] = useState<string>("")
    const [formData, setFormData] = useState<ShortcutDto>(props.shortcut?.getDto() || {
        type: SHORTCUT_TYPE.FILTER,
        attributes: {...DEFAULT_ATTRIBUTES}
    } as ShortcutDto)

    const onFormDataChange = (formData: ShortcutDto) => {
        setFormData({...formData})
    }

    const onTabSelect = (eventKey: string | null) => {
        if (!eventKey) return

        setFormData(prevData => ({
            ...prevData,
            type: `shortcut-${eventKey}`,
            attributes: {
                ...DEFAULT_ATTRIBUTES,
            }
        } as ShortcutDto))
    }

    const closeModals = (event) => {
        event.preventDefault()
        props.onClose()
    }

    const updateShortcuts = (event) => {
        event.preventDefault()
        const form = event.currentTarget
        if (!form.checkValidity()) {
            event.stopPropagation()
            return
        }

        const payload = {
            ...formData.attributes,
            savedFilterId: formData.attributes.savedFilter?.id,
            shortcutType: formData.type,
            actionType: formData.attributes.actionType,
            rank: props.ranks ? props.ranks[formData.type] : undefined
        }

        if (props.shortcut) {
            ShortcutService.updateShortcut(props.shortcut.getId(), payload).then((r) => {
                if (!isError(r)) {
                    props.handleShortcutsList(payload.shortcutType, r, "update")
                    props.onClose()
                } else {
                    setErrorMsg(r.message)
                }
            })
        } else {
            ShortcutService.createShortcut(payload).then((r) => {
                if (!isError(r)) {
                    props.handleShortcutsList(payload.shortcutType, r, "create")
                    props.onClose()
                } else {
                    setErrorMsg(r.message)
                }
            })
        }
    }

    const getShortcutSettings = (shortcutType: SHORTCUT_TYPE) => {
        switch (shortcutType) {
        case SHORTCUT_TYPE.FILTER:
            return <ShortcutFilterSettings value={formData} onChange={onFormDataChange}/>
        case SHORTCUT_TYPE.ACTION:
            return <ShortcutActionSettings value={formData} onChange={onFormDataChange}/>
        }
    }

    const isSaveBtnDisabled = () => {
        return (formData.type == SHORTCUT_TYPE.ACTION
                && [SHORTCUT_ACTION_TYPE.CLONE_QUOTE, SHORTCUT_ACTION_TYPE.CLONE_INVOICE].includes(formData.attributes.actionType)
                && formData.attributes.cloneId == "") ||
            ([SHORTCUT_TYPE.ACTION].includes(formData.type) &&
                (!getIconValue(formData.attributes.actionType) ||
                    !formData.attributes.name)) ||
            ([SHORTCUT_TYPE.FILTER].includes(formData.type) &&
                (!formData.attributes.name || !formData.attributes.color ||
                    !formData.attributes.savedFilter || !formData.attributes.savedFilter.id))
    }

    const hasReachedMaxNumberOfFilterTiles = () => {
        return props.ranks && props.ranks["shortcut-filter"] >= 8
    }

    return (
        <Modal show={true} onHide={props.onClose} centered={true}>
            <Form noValidate onSubmit={updateShortcuts}>

                <Modal.Header closeButton>
                    <Modal.Title>
                        {props.shortcut ?
                            <h5 className="modal-title pb-0 mb-0 border-0" id="setSettingModalTitleUpdate">
                                <FormattedMessage
                                    id="shortcutFilter.set_setting_modal.title_update"
                                    defaultMessage="Update tile"
                                />
                            </h5>
                            :
                            <h5 className="modal-title pb-0 mb-0 border-0" id="setSettingModalTitleCreate">
                                <FormattedMessage
                                    id="shortcutFilter.set_setting_modal.title_create"
                                    defaultMessage="Add a new tile"
                                />
                            </h5>
                        }
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {props.shortcut ?
                        getShortcutSettings(props.shortcut.getShortcutType())
                        :
                        <div className="pt-1">
                            <Tabs defaultActiveKey="filter" onSelect={onTabSelect}>

                                <Tab eventKey="filter" title="Filter tile" unmountOnExit>
                                    {hasReachedMaxNumberOfFilterTiles() ?
                                        <div className="mt-3 p-3">
                                            <FormattedMessage
                                                tagName="p"
                                                id="shortcutFilter.set_setting_modal.has_reached_max_number_of_filter_tiles"
                                                defaultMessage="Reached the maximum number of filter tiles"
                                            />
                                        </div>
                                        :
                                        <ShortcutFilterSettings value={formData} onChange={onFormDataChange}/>
                                    }
                                </Tab>

                                <Tab eventKey="action" title="Action tile" unmountOnExit>
                                    <ShortcutActionSettings value={formData} onChange={onFormDataChange}/>
                                </Tab>

                            </Tabs>
                        </div>
                    }
                </Modal.Body>

                <Modal.Footer>
                    <div className="text-danger">{errorMsg}</div>
                    <Button type="button" className="btn btn-secondary" onClick={closeModals}>
                        <FormattedMessage
                            id="shortcutFilter.set_setting_modal.close_btn"
                            defaultMessage="Close"
                        />
                    </Button>
                    <Button type="submit" className="btn btn-primary" disabled={isSaveBtnDisabled()}>
                        {props.shortcut ?
                            <FormattedMessage
                                id="shortcutFilter.set_setting_modal.save_tile_btn"
                                defaultMessage="Save tile"
                            />
                            :
                            <FormattedMessage
                                id="shortcutFilter.set_setting_modal.create_tile_btn"
                                defaultMessage="Create tile"
                            />
                        }
                    </Button>
                </Modal.Footer>

            </Form>
        </Modal>
    )
}

export default injectIntl(ShortcutsSettingsModal)


// WEBPACK FOOTER //
// ./src/shortcuts/components/ShortcutsSettingsModal.tsx